import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff } from 'lucide-react';

export default function VaultLockScreen() {
  const { unlockVault, user, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setError('');
    setUnlocking(true);
    try {
      const ok = await unlockVault(password);
      if (!ok) {
        setError('Incorrect password. Please try again.');
      }
    } catch {
      setError('Failed to unlock vault.');
    }
    setUnlocking(false);
  };

  return (
    <div className="flex items-center justify-center h-screen bg-background" data-testid="vault-lock-screen">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent-amber/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-accent-amber" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Vault Locked</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter your account password to decrypt your notes
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Signed in as {user?.email}
          </p>
        </div>

        <form onSubmit={handleUnlock} className="space-y-4">
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              autoFocus
              data-testid="vault-unlock-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-400" data-testid="vault-unlock-error">{error}</p>
          )}

          <Button
            type="submit"
            className="w-full bg-accent-amber hover:bg-accent-amber/90 text-black font-medium"
            disabled={unlocking || !password.trim()}
            data-testid="vault-unlock-btn"
          >
            {unlocking ? 'Unlocking...' : 'Unlock Vault'}
          </Button>

          <button
            type="button"
            onClick={logout}
            className="w-full text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors py-2"
            data-testid="vault-logout-btn"
          >
            Sign out and use a different account
          </button>
        </form>
      </div>
    </div>
  );
}
